import { ComponentType, ReactNode, useMemo } from "react";
import { Link } from "react-router-dom";
import { DatasetAccessLevel } from "datasets/types/enums/access-level";
import { DatasetListItem } from "datasets/types/interfaces/datasetListItem";
import { resolveDatasetAccentColor } from "datasets/utils/color";
import { formatDistanceToNowStrict } from "date-fns";
import { Clock, HardDrive, LucideIcon, Table2 } from "lucide-react";
import pluralize from "pluralize";
import prettyBytes from "pretty-bytes";
import { CardIllustration } from "~/assets/icons";
import { type ViewMode } from "~/hooks/useViewMode";
import { stripMarkdownToText } from "~/lib/stripMarkdownToText";
import { cn } from "~/lib/utils";
import { FavoriteButton } from "~/pages/manifold-nextgen/components/FavoriteButton";
import { nextGenDatasetDetailPath } from "~/router/constants";
import { AccessLevelBadge } from "./AccessLevelBadge";
import { DatasetIcon } from "./DatasetIcon";

interface StatProps {
  icon: LucideIcon | ComponentType<{ className?: string }>;
  children: ReactNode;
  className?: string;
}

const Stat = ({ icon: Icon, children, className }: StatProps) => {
  return (
    <span className={cn("flex items-center gap-1 text-xs", className)}>
      <Icon className="size-3.5 shrink-0" />
      {children}
    </span>
  );
};

interface DatasetCardProps {
  dataset: DatasetListItem;
  view?: ViewMode;
  favorite?: boolean;
  // When provided, renders a favorite toggle. Omit to hide the affordance entirely.
  onToggleFavorite?: () => void;
}

// A non-favorite toggle stays hidden until the row/card is hovered or the
// button is focused; a favorited one is always visible.
const favoriteVisibility = (favorite: boolean) =>
  !favorite && "opacity-0 group-hover:opacity-100 focus-visible:opacity-100";

export const DatasetCard = ({
  dataset,
  view = "card",
  favorite = false,
  onToggleFavorite,
}: DatasetCardProps) => {
  // Preview datasets omit `components`, so their table/byte stats are hidden.
  const isPreviewDataset = dataset.access_level === DatasetAccessLevel.PREVIEW;
  const tableCount = dataset.components?.tables?.count ?? 0;
  const totalBytes = prettyBytes(dataset.components?.tables?.total_bytes ?? 0);
  const description = useMemo(
    () => (dataset.description ? stripMarkdownToText(dataset.description) : ""),
    [dataset.description]
  );
  const updatedAt = dataset.updated_at
    ? formatDistanceToNowStrict(new Date(dataset.updated_at), { addSuffix: true })
    : null;

  const statsContent = (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-muted-foreground">
      {!isPreviewDataset && (
        <>
          <Stat icon={Table2}>{`${tableCount} ${pluralize("Table", tableCount)}`}</Stat>
          <Stat icon={HardDrive}>{totalBytes}</Stat>
        </>
      )}
      {updatedAt && <Stat icon={Clock}>{updatedAt}</Stat>}
    </div>
  );

  // Fixed-width columns (vs. the card view's flex-wrap) so each stat lines up
  // across rows regardless of how wide its own value is.
  const listStatsContent = (
    <div className="flex shrink-0 items-center gap-3 text-muted-foreground">
      {isPreviewDataset ? (
        <>
          <span className="w-24" aria-hidden="true" />
          <span className="w-20" aria-hidden="true" />
        </>
      ) : (
        <>
          <Stat icon={Table2} className="w-24">
            {`${tableCount} ${pluralize("Table", tableCount)}`}
          </Stat>
          <Stat icon={HardDrive} className="w-20">
            {totalBytes}
          </Stat>
        </>
      )}
      <Stat icon={Clock} className="w-28">
        {updatedAt}
      </Stat>
    </div>
  );

  if (view === "list") {
    return (
      <Link
        to={nextGenDatasetDetailPath(dataset.id)}
        data-testid="nextgen-dataset-card"
        className="group relative block rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <div
          className={cn(
            "flex items-center gap-4 rounded-lg bg-card px-4 py-5",
            "transition-colors duration-150 group-hover:bg-muted"
          )}
        >
          <DatasetIcon dataset={dataset} size={20} />
          <div className="min-w-0 flex-1">
            <h3 className="truncate text-sm font-semibold text-foreground">{dataset.title}</h3>
            <p
              className={cn(
                "truncate text-xs text-muted-foreground",
                !description && "italic text-muted-foreground/60"
              )}
            >
              {description || "No description"}
            </p>
          </div>
          <div className="w-24 shrink-0">
            <AccessLevelBadge accessLevel={dataset.access_level} hideDefault />
          </div>
          <div className="flex w-80 shrink-0 justify-end">{listStatsContent}</div>
          {onToggleFavorite && (
            <FavoriteButton
              favorite={favorite}
              onToggle={onToggleFavorite}
              className={cn("shrink-0", favoriteVisibility(favorite))}
            />
          )}
        </div>
      </Link>
    );
  }

  const accentColor = resolveDatasetAccentColor(dataset.accent_color);

  return (
    <Link
      to={nextGenDatasetDetailPath(dataset.id)}
      data-testid="nextgen-dataset-card"
      className="group relative block h-full rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring transition-transform duration-200 ease-[cubic-bezier(0.165,0.84,0.44,1)] hover:scale-[1.02]"
    >
      <article
        className={cn(
          "relative flex h-full flex-col rounded-lg border border-border bg-card",
          "shadow-[0_1px_2px_rgba(15,23,42,0.03)]"
        )}
      >
        <div
          className="absolute inset-x-0 -top-px h-12 rounded-t-lg"
          style={{ backgroundColor: accentColor.bg }}
          aria-hidden="true"
        />
        <div
          className={cn(
            "relative mt-[34px] flex flex-1 flex-col gap-3",
            "rounded-lg rounded-tl-none bg-card px-6 pb-3 pt-4"
          )}
        >
          <CardIllustration
            aria-hidden="true"
            className="pointer-events-none absolute -top-6 left-0 z-10 h-[29px] w-[95px] text-card"
          />
          {onToggleFavorite && (
            <FavoriteButton
              favorite={favorite}
              onToggle={onToggleFavorite}
              className={cn("absolute right-2.5 top-2.5 z-20", favoriteVisibility(favorite))}
            />
          )}
          <h3 className="line-clamp-1 pr-8 text-base font-semibold leading-tight text-foreground">
            {dataset.title}
          </h3>
          <p
            className={cn(
              "line-clamp-4 text-sm leading-normal text-muted-foreground",
              !description && "italic text-muted-foreground/60"
            )}
          >
            {description || "No description"}
          </p>
          <div className="mt-auto flex flex-wrap items-center gap-2">
            <AccessLevelBadge accessLevel={dataset.access_level} hideDefault />
            <div className="ml-auto">{statsContent}</div>
          </div>
        </div>
      </article>
      <div
        aria-hidden="true"
        className={cn(
          "pointer-events-none absolute inset-0 rounded-lg",
          "opacity-0 shadow-[0_4px_10px_rgba(15,23,42,0.06)]",
          "transition-opacity duration-200 ease-[cubic-bezier(0.165,0.84,0.44,1)] group-hover:opacity-100"
        )}
      />
    </Link>
  );
};
